import { useEffect, useState, type ReactNode } from 'react';
import { api, formatBytes } from '../lib/api.js';
import { TrashPanel } from '../components/TrashPanel.js';
import { ProcessingPanel } from '../components/ProcessingPanel.js';
import { StoragePanel } from '../components/StoragePanel.js';
import { MaintenancePanel } from '../components/MaintenancePanel.js';
import { SidecarPanel } from '../components/SidecarPanel.js';

type Users = Awaited<ReturnType<typeof api.adminUsers>>['users'];
type RoleMap = Awaited<ReturnType<typeof api.adminRoleMap>>;
type Stats = Awaited<ReturnType<typeof api.adminStats>>;
type Health = Awaited<ReturnType<typeof api.health>>;

export function AdminPage(): ReactNode {
  const [tab, setTab] = useState<
    'processing' | 'storage' | 'users' | 'roles' | 'trash' | 'cleanup' | 'sidecars' | 'system'
  >('processing');
  const [users, setUsers] = useState<Users>([]);
  const [roleMap, setRoleMap] = useState<RoleMap | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [newRoleId, setNewRoleId] = useState('');
  const [newRoleLabel, setNewRoleLabel] = useState('');
  const [newRoleTier, setNewRoleTier] = useState(20);

  const reload = (): void => {
    void api.adminUsers(search || undefined).then((r) => setUsers(r.users)).catch(() => {});
    void api.adminRoleMap().then(setRoleMap).catch(() => {});
    void api.adminStats().then(setStats).catch(() => {});
    void api.health().then(setHealth).catch(() => setHealth(null));
  };

  useEffect(reload, [search]);

  const setTier = async (id: string, tier: number | null): Promise<void> => {
    setError(null);
    try {
      await api.adminUpdateUser(id, { tierOverride: tier });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const addRole = async (): Promise<void> => {
    setError(null);
    try {
      await api.adminSetRole({
        discordRoleId: newRoleId.trim(),
        label: newRoleLabel.trim(),
        tier: newRoleTier,
      });
      setNewRoleId('');
      setNewRoleLabel('');
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the mapping');
    }
  };

  const tiers = roleMap?.availableTiers ?? [];

  return (
    <div className="body no-sidebar">
      <div className="main" style={{ maxWidth: 1000, margin: '0 auto', width: '100%' }}>
        <h2>Administration</h2>

        {stats?.webBuild.stale && (
          <div className="notice warning">
            <strong>The web interface is out of date.</strong> The built bundle
            being served is {stats.webBuild.staleByMinutes} minutes older than
            the source ({stats.webBuild.newestSource}). Features added since
            that build will be missing from every page until someone runs{' '}
            <code>npm run web:build</code> on the server and restarts the API.
          </div>
        )}

        <div className="flex" style={{ marginBottom: 16 }}>
          {(['processing', 'storage', 'users', 'roles', 'trash', 'cleanup', 'sidecars', 'system'] as const).map((key) => (
            <button
              key={key}
              className={`btn${tab === key ? ' primary' : ''}`}
              onClick={() => setTab(key)}
              style={{ textTransform: 'capitalize' }}
            >
              {key}
            </button>
          ))}
        </div>

        {error && <div className="notice error">{error}</div>}

        {tab === 'users' && (
          <div className="panel">
            <h3>Members</h3>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              style={{ maxWidth: 280, marginBottom: 12 }}
            />
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th style={{ width: 130 }}>Tier</th>
                  <th style={{ width: 110 }}>Uploads</th>
                  <th style={{ width: 100 }}>Stored</th>
                  <th style={{ width: 90 }}>Active</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {user.global_name ?? user.username}
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        @{user.username}
                        {user.last_login_at &&
                          ` — last seen ${new Date(user.last_login_at).toLocaleDateString()}`}
                      </div>
                    </td>
                    <td>
                      <select
                        value={user.tier_override ?? ''}
                        onChange={(e) =>
                          void setTier(user.id, e.target.value === '' ? null : Number(e.target.value))
                        }
                        title={
                          user.tier_override === null
                            ? `Derived from Discord roles (${user.tierName})`
                            : 'Manually overridden'
                        }
                      >
                        <option value="">From roles ({user.tierName})</option>
                        {tiers.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{user.upload_count}</td>
                    <td>{formatBytes(Number(user.bytes_uploaded))}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={user.is_active}
                        style={{ width: 'auto' }}
                        onChange={(e) => {
                          void api
                            .adminUpdateUser(user.id, { isActive: e.target.checked })
                            .then(reload)
                            .catch((err: Error) => setError(err.message));
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'roles' && (
          <>
            <div className="panel">
              <h3>Discord role → permission tier</h3>
              <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 0 }}>
                Anyone in the server gets <strong>viewer</strong> automatically. These mappings
                grant more. When someone holds several mapped roles, the highest tier wins.
                Changes apply to everyone holding the role immediately.
              </p>
              <table>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Discord role ID</th>
                    <th style={{ width: 110 }}>Tier</th>
                    <th style={{ width: 80 }}>Members</th>
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {roleMap?.mappings.map((mapping) => (
                    <tr key={mapping.discord_role_id}>
                      <td>{mapping.label}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {mapping.discord_role_id}
                      </td>
                      <td>
                        <select
                          value={mapping.tier}
                          onChange={(e) => {
                            void api
                              .adminSetRole({
                                discordRoleId: mapping.discord_role_id,
                                label: mapping.label,
                                tier: Number(e.target.value),
                              })
                              .then(reload);
                          }}
                        >
                          {tiers.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{mapping.member_count}</td>
                      <td>
                        <button
                          className="btn sm danger"
                          onClick={() => {
                            void api.adminDeleteRole(mapping.discord_role_id).then(reload);
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="panel">
              <h3>Add a mapping</h3>
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 0 }}>
                In Discord, enable Developer Mode, then right-click a role → Copy Role ID.
              </p>
              <div className="flex" style={{ flexWrap: 'wrap' }}>
                <input
                  value={newRoleLabel}
                  onChange={(e) => setNewRoleLabel(e.target.value)}
                  placeholder="Label, e.g. Media Team"
                  style={{ maxWidth: 220 }}
                />
                <input
                  value={newRoleId}
                  onChange={(e) => setNewRoleId(e.target.value)}
                  placeholder="Role ID"
                  style={{ maxWidth: 220, fontFamily: 'monospace' }}
                />
                <select
                  value={newRoleTier}
                  onChange={(e) => setNewRoleTier(Number(e.target.value))}
                  style={{ width: 'auto' }}
                >
                  {tiers.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <button
                  className="btn primary"
                  onClick={() => void addRole()}
                  disabled={!newRoleId.trim() || !newRoleLabel.trim()}
                >
                  Add
                </button>
              </div>
            </div>
          </>
        )}

        {tab === 'processing' && <ProcessingPanel />}

        {tab === 'storage' && <StoragePanel />}

        {tab === 'trash' && <TrashPanel />}

        {tab === 'cleanup' && <MaintenancePanel />}

        {tab === 'sidecars' && <SidecarPanel />}

        {tab === 'system' && (
          <>
            <div className="panel">
              <h3>Archive</h3>
              <dl className="kv">
                <dt>Files</dt>
                <dd>{stats?.totals.assets ?? '—'}</dd>
                <dt>Stored</dt>
                <dd>{stats ? formatBytes(Number(stats.totals.bytes)) : '—'}</dd>
                <dt>Folders</dt>
                <dd>{stats?.totals.folders ?? '—'}</dd>
                <dt>Members</dt>
                <dd>{stats?.totals.users ?? '—'}</dd>
              </dl>
            </div>

            <div className="panel">
              <h3>By type</h3>
              <table>
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Count</th>
                    <th>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.byKind.map((row) => (
                    <tr key={row.kind}>
                      <td>{row.kind}</td>
                      <td>{row.count}</td>
                      <td>{formatBytes(Number(row.bytes))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="panel">
              <h3>Services</h3>
              <dl className="kv">
                <dt>NAS storage</dt>
                <dd style={{ color: health?.storage.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {health?.storage.ok ? 'mounted and writable' : 'UNAVAILABLE'}
                  {health?.storage.mediaRoot && (
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                      {health.storage.mediaRoot}
                    </div>
                  )}
                </dd>
                <dt>GPU worker</dt>
                <dd
                  style={{
                    color:
                      health?.visionService === null
                        ? 'var(--text-dim)'
                        : health?.visionService
                          ? 'var(--success)'
                          : 'var(--danger)',
                  }}
                >
                  {health?.visionService === null
                    ? 'disabled'
                    : health?.visionService
                      ? 'running'
                      : 'not reachable'}
                </dd>
                <dt>Sync clients</dt>
                <dd>{health?.websocketClients ?? 0} connected</dd>
              </dl>
            </div>

            {health?.queues && (
              <div className="panel">
                <h3>Job queues</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Queue</th>
                      <th>Waiting</th>
                      <th>Active</th>
                      <th>Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(health.queues).map(([name, counts]) => (
                      <tr key={name}>
                        <td>{name}</td>
                        <td>{counts.waiting ?? 0}</td>
                        <td>{counts.active ?? 0}</td>
                        <td style={{ color: counts.failed ? 'var(--danger)' : undefined }}>
                          {counts.failed ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
